import { Flex, SimpleSpinner, Text } from '@buffer-mono/popcorn'

import { selectIntegrationImport } from '~publish/legacy/integrations-bar/state/integrationBarSelectors'
import { useAppSelector } from '~publish/legacy/store'
import { selectPendingUploads } from '~publish/legacy/uploads/state/selectors'

import styles from './IntegrationImportThumbnail.module.css'

import type { JSX } from 'react'

/**
 * Placeholder tile for a file an integration is still handing over.
 *
 * Uploadcare first copies the picked file from the integration into its own
 * storage, then we fetch it into the browser. Our uploader has no upload of its
 * own to report until both finish. For a video that wait reaches about a
 * minute, so this tile stands in for the pending thumbnail that follows it.
 *
 * @see CORE-6872
 */
export const IntegrationImportThumbnail = ({
  uploaderId,
}: {
  uploaderId: string
}): JSX.Element | null => {
  const activeImport = useAppSelector(selectIntegrationImport)
  const pendingUploads = useAppSelector((state) =>
    selectPendingUploads(state, uploaderId),
  )

  // The import belongs to one uploader. Another uploader showing the same wait
  // would claim a file that is not coming to it.
  if (activeImport?.uploaderId !== uploaderId) {
    return null
  }

  // As soon as the uploader holds the file it renders its own thumbnail, with
  // real progress. Two tiles for one file would read as two files.
  if (pendingUploads.length > 0) {
    return null
  }

  return (
    <Flex
      as="output"
      className={styles.thumbnail}
      direction="column"
      align="center"
      justify="center"
      gap="2xs"
      data-testid="integration-import-thumbnail"
    >
      <SimpleSpinner size="medium" />
      <Text size="sm" color="subtle" align="center">
        Importing from {activeImport.source}
      </Text>
    </Flex>
  )
}
